import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import WorkspaceLoader from './WorkspaceLoader';
import {
  Grid, List, FileText, ChevronLeft, ChevronRight, Upload, ArrowLeft, Search
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import { db, storage } from '@/firebase/config';
import { collection, getDocs, query as fbQuery, orderBy, limit, startAfter, setDoc, deleteDoc, doc, where } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { ref, getDownloadURL } from 'firebase/storage';

import {
  ResourceCard,
  ResourceFilters,
  ResourcePreview,
  BookUploadForm,
  PDFReader,
  resourceCategoriesConfig,
  iconMap,
  Resource,
  FilterOptions
} from './IndependentResourceComponents';

export default function IndependentResourcesView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlSearchQuery = searchParams.get('search') || '';
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
    const [resources, setResources] = useState<Resource[]>([]);
    const [lastVisible, setLastVisible] = useState<any>(null);
    const [firstVisible, setFirstVisible] = useState<any>(null);
    const [pageHistory, setPageHistory] = useState<any[]>([]); // for previous pages
  const [activeTab, setActiveTab] = useState('overview');
  const [filters, setFilters] = useState<FilterOptions>({
    subject: [], grade: [], examType: [], type: []
  });
  
  const [previewResource, setPreviewResource] = useState<Resource | null>(null);
  const [viewResource, setViewResource] = useState<Resource | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sortBy, setSortBy] = useState<'title' | 'uploadDate' | 'downloadCount'>('title');
  const [currentPage, setCurrentPage] = useState(1);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const resourcesPerPage = 12;
  
  const [searchQuery, setSearchQuery] = useState<string>(urlSearchQuery);
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<Resource[] | null>(null);
  const [isLoadingSearch, setIsLoadingSearch] = useState(false);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [resourceCategories, setResourceCategories] = useState(
    resourceCategoriesConfig.map(cat => ({ ...cat, count: 0 }))
  );
  
  const [userId, setUserId] = useState<string | null>(null);
  const [userBookmarks, setUserBookmarks] = useState<Set<string>>(new Set());
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  // --- Effects & Logic ---

  useEffect(() => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (user) {
        setUserId(user.uid);
        // Fetch bookmarks
        const fetchBookmarks = async () => {
             const bookmarksRef = collection(db, 'users', user.uid, 'bookmarks');
             const s = await getDocs(bookmarksRef);
             setUserBookmarks(new Set(s.docs.map(d => d.id)));
        };
        fetchBookmarks();
        // Load category counts on mount
        const loadCategoryCount = async () => {
          setIsLoadingCategories(true);
          try {
            const allResourcesSnap = await getDocs(collection(db, 'global_resources'));
            const allResources = allResourcesSnap.docs.map(doc => ({ ...doc.data(), id: doc.id } as Resource));
            const newCats = resourceCategoriesConfig.map(cat => {
              if (cat.id === 'bookmarks') return { ...cat, count: userBookmarks.size };
              const count = allResources.filter(r => r.type === cat.type).length;
              return { ...cat, count };
            });
            setResourceCategories(newCats);
          } catch (error) {
            console.error('Error loading category counts:', error);
          } finally {
            setIsLoadingCategories(false);
          }
        };
        loadCategoryCount();
    }
  }, []);


    // Fetch resources for the current page
    useEffect(() => {
        const fetchResources = async () => {
            setIsLoading(true);
            let q;
            if (currentPage === 1) {
                q = fbQuery(collection(db, 'global_resources'), orderBy('uploadDate', 'desc'), limit(resourcesPerPage));
            } else if (lastVisible) {
                q = fbQuery(collection(db, 'global_resources'), orderBy('uploadDate', 'desc'), startAfter(lastVisible), limit(resourcesPerPage));
            } else {
                setIsLoading(false);
                return;
            }
            const snapshot = await getDocs(q);
            const docs = snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as Resource));
            setResources(docs);
            setFirstVisible(snapshot.docs[0]);
            setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
            setIsLoading(false);
            // Track page history for going back
            if (currentPage === 1) {
                setPageHistory([snapshot.docs[0]]);
            } else {
                setPageHistory(prev => {
                    const newHistory = [...prev];
                    newHistory[currentPage - 1] = snapshot.docs[0];
                    return newHistory;
                });
            }
        };
        // Only fetch paginated resources if we're in overview or viewing all resources
        if (activeTab === 'overview') {
            fetchResources();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, activeTab]);

  // Fetch all resources when category tab changes
  useEffect(() => {
    if (activeTab !== 'overview') {
      const fetchAllResources = async () => {
        setIsLoading(true);
        try {
          const allResourcesSnap = await getDocs(collection(db, 'global_resources'));
          const allResources = allResourcesSnap.docs.map(doc => ({ ...doc.data(), id: doc.id } as Resource));
          setResources(allResources);
        } catch (error) {
          console.error('Error fetching all resources:', error);
        } finally {
          setIsLoading(false);
        }
      };
      fetchAllResources();
    }
  }, [activeTab]);

  useEffect(() => {
     const loadCategoryCount = async () => {
       setIsLoadingCategories(true);
       try {
         const allResourcesSnap = await getDocs(collection(db, 'global_resources'));
         const allResources = allResourcesSnap.docs.map(doc => ({ ...doc.data(), id: doc.id } as Resource));
         const newCats = resourceCategoriesConfig.map(cat => {
           if (cat.id === 'bookmarks') return { ...cat, count: userBookmarks.size };
           const count = allResources.filter(r => r.type === cat.type).length;
           return { ...cat, count };
         });
         setResourceCategories(newCats);
       } catch (error) {
         console.error('Error loading category counts:', error);
       } finally {
         setIsLoadingCategories(false);
       }
     };
     loadCategoryCount();
  }, [userBookmarks]);

  useEffect(() => {
    if (searchQuery.trim()) {
      performFirebaseSearch(searchQuery);
    } else {
      setSearchResults(null);
    }
  }, [searchQuery]);

  const performFirebaseSearch = async (q: string) => {
      const term = q.toLowerCase();
      if (!term) { setSearchResults(null); return; }
      
      setIsLoadingSearch(true);
      try {
        const allResourcesSnap = await getDocs(collection(db, 'global_resources'));
        const allResources = allResourcesSnap.docs.map(doc => ({ ...doc.data(), id: doc.id } as Resource));
        const results = allResources.filter(r => 
          r.title.toLowerCase().includes(term) || 
          r.subject.toLowerCase().includes(term) ||
          r.tags.some(t => t.toLowerCase().includes(term))
        );
        setSearchResults(results);
      } catch (error) {
        console.error('Error searching resources:', error);
        setSearchResults([]);
      } finally {
        setIsLoadingSearch(false);
      }
  };

    // Filtering is now done on the loaded page only
    const filteredResources = useMemo(() => {
        const rawSource = searchResults ?? resources;
        const source = rawSource.map(r => ({ ...r, isBookmarked: userBookmarks.has(r.id) }));
        let result = source.filter(resource => {
            if (activeTab === 'bookmarks' && !resource.isBookmarked) return false;
            if (['books', 'papers', 'guides', 'notes'].includes(activeTab)) {
                const types: Record<string, string> = { books: 'book', papers: 'paper', guides: 'guide', notes: 'notes' };
                if (resource.type !== types[activeTab]) return false;
            }
            const query = (searchQuery || urlSearchQuery).toLowerCase();
            if (query) {
                const match = resource.title.toLowerCase().includes(query) ||
                    resource.subject.toLowerCase().includes(query) ||
                    resource.tags.some(t => t.toLowerCase().includes(query));
                if (!match) return false;
            }
            if (filters.subject.length > 0 && !filters.subject.includes(resource.subject)) return false;
            if (filters.grade.length > 0 && !filters.grade.includes(resource.grade)) return false;
            if (filters.examType.length > 0 && !filters.examType.includes(resource.examType)) return false;
            if (filters.type.length > 0 && !filters.type.includes(resource.type)) return false;
            return true;
        });
        result.sort((a, b) => {
            if (sortBy === 'title') return a.title.localeCompare(b.title);
            if (sortBy === 'uploadDate') return new Date(b.uploadDate).getTime() - new Date(a.uploadDate).getTime();
            return b.downloadCount - a.downloadCount;
        });
        return result;
    }, [resources, searchResults, activeTab, filters, sortBy, searchQuery, urlSearchQuery, userBookmarks]);

    const activeFiltersCount = Object.values(filters).flat().length;
    const paginatedResources = filteredResources; // Already paginated by Firestore

  const handleBookmark = async (id: string) => {
      if (!userId) return;
      const isBookmarked = userBookmarks.has(id);
      const ref = doc(db, 'users', userId, 'bookmarks', id);
      try {
          if (isBookmarked) {
              await deleteDoc(ref);
              setUserBookmarks(prev => { const n = new Set(prev); n.delete(id); return n; });
          } else {
              const res = resources.find(r => r.id === id);
              if (res) await setDoc(ref, { resourceId: id, title: res.title });
              setUserBookmarks(prev => { const n = new Set(prev); n.add(id); return n; });
          }
      } catch (e) {
          console.error(e);
      }
  };

  const handleDownload = async (resource: Resource) => {
    setDownloadingId(resource.id);
    try {
      toast({
        title: 'Fetching file...',
        description: `Preparing "${resource.title}" for download securely. Please wait.`,
        className: "bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800 text-green-800 dark:text-green-300",
        duration: 4000,
      });

      const storageRef = ref(storage, resource.pdfUrl);
      const downloadUrl = await getDownloadURL(storageRef);
      
      const response = await fetch(downloadUrl);
      if (!response.ok) throw new Error('Network response was not ok');
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = blobUrl;
      const cleanUrl = resource.pdfUrl.split('?')[0];
      const fileExtension = cleanUrl.split('.').pop()?.toLowerCase() || 'pdf';
      link.download = `${resource.title}.${fileExtension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    } catch (error) {
      console.error('Error downloading resource:', error);
      toast({
        title: 'Download Failed',
        description: 'Unable to download the resource. Please check the file URL or try again later.',
        className: "bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800 text-red-800 dark:text-red-300",
        duration: 5000,
      });
    } finally {
      setDownloadingId(null);
    }
  };

  const handleView = (resource: Resource) => {
    setViewResource(resource);
  };

  const getTabTitle = () => {
    switch (activeTab) {
      case 'overview': return 'Overview';
      case 'books': return 'Books';
      case 'papers': return 'Past Papers';
      case 'guides': return 'Revision Guides';
      case 'notes': return 'Notes';
      case 'bookmarks': return 'Bookmarked Resources';
      default: return 'Resources';
    }
  };

  if (viewResource) {
      return (
          <PDFReader 
            pdfUrl={viewResource.pdfUrl} 
            title={viewResource.title} 
            onBack={() => { setViewResource(null); setIsFullscreen(false); }} 
            setIsFullscreen={setIsFullscreen} 
          />
      );
  }

  return (
    <>
    <div className="flex-1 space-y-3 p-2 sm:p-4 md:p-6 w-full min-h-full">
            {/* Header / Top Bar */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
                <div className="flex items-center gap-2">
                    {activeTab !== 'overview' && (
                    <Button
                        variant="outline"
                        onClick={() => setActiveTab('overview')}
                        className="mr-2 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300"
                    >
                        <ArrowLeft className="h-4 w-4 mr-0 md:mr-2" />
                        <span className="hidden md:inline">Back</span>
                    </Button>
                    )}
                    <h1 className="text-xl md:text-2xl font-semibold text-gray-900 dark:text-white">{getTabTitle()}</h1>
                </div>

                <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                    <div className="relative w-full sm:w-auto">
                    <Input
                        placeholder="Search resources..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full sm:w-[200px] bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300"
                    />
                    <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    </div>
                    
                    <Button
                        variant={viewMode === 'grid' ? 'default' : 'outline'}
                        size="icon"
                        onClick={() => setViewMode('grid')}
                        className={viewMode === 'grid' ? 'bg-[#2563EB] hover:bg-[#1E40AF]' : 'bg-white text-gray-700'}
                    >
                        <Grid className="h-4 w-4" />
                    </Button>
                    <Button
                        variant={viewMode === 'list' ? 'default' : 'outline'}
                        size="icon"
                        onClick={() => setViewMode('list')}
                        className={viewMode === 'list' ? 'bg-[#2563EB] hover:bg-[#1E40AF]' : 'bg-white text-gray-700'}
                    >
                        <List className="h-4 w-4" />
                    </Button>

                    <Select value={sortBy} onValueChange={(v) => setSortBy(v as any)}>
                    <SelectTrigger className="w-[140px] bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300">
                        <SelectValue placeholder="Sort by" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                        <SelectItem value="title" className="text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#1E293B] cursor-pointer">Sort by Title</SelectItem>
                        <SelectItem value="uploadDate" className="text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#1E293B] cursor-pointer">Sort by Date</SelectItem>
                        <SelectItem value="downloadCount" className="text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#1E293B] cursor-pointer">Sort by Downloads</SelectItem>
                    </SelectContent>
                    </Select>

                    <Button onClick={() => setIsUploadOpen(true)} className="bg-[#2563EB] hover:bg-[#1E40AF]">
                    <Upload className="h-4 w-4 mr-2" /> Upload
                    </Button>
                </div>
            </div>

            {/* Content Area */}
            {activeTab === 'overview' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                    {resourceCategories.map((category) => {
                    const Icon = iconMap[category.icon as keyof typeof iconMap] || FileText;
                    return (
                        <Card
                            key={category.id}
                            className="group cursor-pointer bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B] hover:shadow-md transition-shadow"
                            onClick={() => {
                                setActiveTab(category.id);
                                setSearchResults(null);
                                setSearchQuery('');
                                setCurrentPage(1);
                            }}
                        >
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-[#2563EB]">
                                {category.name}
                            </CardTitle>
                            <Icon className="h-5 w-5 text-[#2563EB]" />
                        </CardHeader>
                        <CardContent>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">{category.count}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Resources available</p>
                        </CardContent>
                        </Card>
                    );
                    })}
                </div>
            )}

            {activeTab !== 'overview' && (
                <div className="mb-6">
                    <ResourceFilters
                        filters={filters}
                        onFilterChange={setFilters}
                        activeFiltersCount={activeFiltersCount}
                    />
                </div>
            )}

            {(isLoading || isLoadingSearch || isLoadingCategories) ? (
                <WorkspaceLoader minHeight="min-h-[200px]" />
            ) : filteredResources.length === 0 ? (
                <div className="text-center py-12">
                    <FileText className="mx-auto h-12 w-12 text-gray-500 dark:text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium">No resources found</h3>
                    <p className="text-muted-foreground">Try adjusting your filters or search query.</p>
                </div>
            ) : (
                <div className={viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4' : 'space-y-4'}>
                    {paginatedResources.map((resource) => (
                        <ResourceCard
                            key={resource.id}
                            resource={resource}
                            onBookmark={handleBookmark}
                            onDownload={handleDownload}
                            onView={handleView}
                            viewMode={viewMode}
                            isDownloading={downloadingId === resource.id}
                        />
                    ))}
                </div>
            )}
            
            {/* Pagination ... (simplified here but essential for replica) */}
                        <div className="flex justify-center items-center gap-2 mt-6">
                            <Button variant="outline" size="sm" onClick={() => { setIsLoading(true); setCurrentPage(p => Math.max(1, p-1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }} disabled={currentPage===1}><ChevronLeft className="h-4 w-4"/></Button>
                            <span className="text-sm">Page {currentPage}</span>
                            <Button variant="outline" size="sm" onClick={() => { setIsLoading(true); setCurrentPage(p => p+1); window.scrollTo({ top: 0, behavior: 'smooth' }); }} disabled={paginatedResources.length < resourcesPerPage}><ChevronRight className="h-4 w-4"/></Button>
                        </div>


        <Dialog open={isUploadOpen} onOpenChange={setIsUploadOpen}>
            <DialogContent className="max-w-3xl w-[calc(100%-2rem)]">
                <DialogHeader><DialogTitle>Upload New Resource</DialogTitle></DialogHeader>
                <BookUploadForm onClose={() => setIsUploadOpen(false)} />
            </DialogContent>
        </Dialog>

        <ResourcePreview
            resource={previewResource}
            isOpen={!!previewResource}
            onClose={() => setPreviewResource(null)}
            onDownload={handleDownload}
            onBookmark={handleBookmark}
            onView={(res) => setViewResource(res)}
        />
    </div>
    </>
  );
}
